import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {WorkloadDefinitionsService} from "app/services/domain/workload-definitions.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";

@Injectable()
export class WorkloadDefinitionVersionsService extends SharedSvc.BaseDefinitionVersionService<Models.WorkloadConfiguration, Models.WorkloadDefinitionVersion, Models.WorkloadDefinition, WorkloadDefinitionVersionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.WorkloadDefinitionVersion, WorkloadDefinitionVersionExtended.newInstance);
    }

    //--//

    protected cachePrefix(): string { return "WorkloadDefinitionVersion"; }

    protected getRaw(id: string): Promise<Models.WorkloadDefinitionVersion>
    {
        return this.api.workloadDefinitionVersionsApi.get(id);
    }

    protected getBatchRaw(ids: string[]): Promise<Models.WorkloadDefinitionVersion[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => WorkloadDefinitionVersionExtended.newIdentity(id))});
        return this.api.workloadDefinitionVersionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }

    public createVersion(model: Models.WorkloadDefinitionVersion): Promise<Models.WorkloadDefinitionVersion>
    {
        return this.domain.apis.workloadDefinitionVersionsApi.create(model);
    }

    async makeHead(id: string): Promise<WorkloadDefinitionVersionExtended>
    {
        let model = await this.api.workloadDefinitionVersionsApi.makeHead(id);
        return this.wrapModel(model);
    }

    async makeRelease(id: string): Promise<WorkloadDefinitionVersionExtended>
    {
        let model = await this.api.workloadDefinitionVersionsApi.makeRelease(id);
        return this.wrapModel(model);
    }
}

export class WorkloadDefinitionVersionExtended extends SharedSvc.ExtendedDefinitionVersionModel<Models.WorkloadConfiguration, Models.WorkloadDefinitionVersion, Models.WorkloadDefinition>
{
    static newInstance(svc: WorkloadDefinitionVersionsService,
                       model: Models.WorkloadDefinitionVersion): WorkloadDefinitionVersionExtended
    {
        return new WorkloadDefinitionVersionExtended(svc, model, "WorkloadDefinitionVersion");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "WorkloadDefinitionVersion",
                                                     sysId: sysId
                                                 });
    }

    //--//

    public async getCloudAccounts(): Promise<string[]>
    {
        const filterOptions = await this.domain.filterOptions.getFilterOptions();
        const cloudAccounts = [];
        for (const tier of this.model.details.tiers)
        {
            for (const cloudAccountId of tier.cloud_account_id)
            {
                if (filterOptions.getStatusFromCloudAccount(cloudAccountId) == Models.ServiceAccountStatus.active)
                {
                    cloudAccounts.push(filterOptions.resolveAccountId(cloudAccountId));
                }
            }
        }

        return cloudAccounts;
    }

    protected definitionSvc(): WorkloadDefinitionsService
    {
        return this.domain.workloadDefinitions;
    }

    protected definitionVersionSvc(): WorkloadDefinitionVersionsService
    {
        return this.domain.workloadDefinitionVersions;
    }
}
